import { getQueryString, isWxWork, isWeiXin } from '@/utils/index'
import {
  parsePartnerAuthFromUrl,
  redirectToWapPartnerLogin,
  getPartnerOAuthBackUrl,
  markPartnerOAuthBackLock,
  installPartnerOAuthBackLock,
  clearPartnerOAuthBackLock,
  replaceToPartnerPath,
} from './crossDomain'
import { isPartnerRoute } from './constants'

const STORAGE_KEY = 'partnerUserInfo'
const CRM_ENTRY_KEY = 'partnerFromCrm'

export function getPartnerUser() {
  try {
    let raw = localStorage.getItem(STORAGE_KEY)
    // 兼容旧版 sessionStorage，读到后迁到 localStorage
    if (!raw) {
      raw = sessionStorage.getItem(STORAGE_KEY)
      if (raw) {
        localStorage.setItem(STORAGE_KEY, raw)
        sessionStorage.removeItem(STORAGE_KEY)
      }
    }
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

export function setPartnerUser(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data || {}))
  sessionStorage.removeItem(STORAGE_KEY)
  if (data?.userId) {
    sessionStorage.removeItem(CRM_ENTRY_KEY)
  }
}

export function clearPartnerUser() {
  localStorage.removeItem(STORAGE_KEY)
  sessionStorage.removeItem(STORAGE_KEY)
  sessionStorage.removeItem(CRM_ENTRY_KEY)
  clearPartnerOAuthBackLock()
}

export function markPartnerCrmEntry() {
  sessionStorage.setItem(CRM_ENTRY_KEY, '1')
}

export function isPartnerCrmEntry() {
  return getQueryString('from') === 'crm' || sessionStorage.getItem(CRM_ENTRY_KEY) === '1'
}

export function getPartnerCrmUserId(store) {
  const id = store?.state?.salesInfo?.sysUserId
  return id != null && id !== '' ? id : null
}

/**
 * 企微 CRM 只读入口：
 * 有 sysUserId + 非友商 OAuth 用户 +（企微 UA / CRM 入口标记 / workwx 域名）
 */
export function isPartnerWxWorkMode(store) {
  if (!isPartnerRoute(window.location.pathname)) return false
  if (getPartnerUser()?.userId) return false
  if (getPartnerCrmUserId(store) == null) return false

  if (isWxWork()) return true
  if (isPartnerCrmEntry()) return true
  if (/workwx/i.test(window.location.host)) return true
  return false
}

export function getPartnerApiParams(store) {
  if (isPartnerWxWorkMode(store)) {
    const crmUserId = getPartnerCrmUserId(store)
    return crmUserId != null ? { crmUserId } : {}
  }
  const user = getPartnerUser()
  if (user?.userId) {
    return { userId: user.userId }
  }
  return {}
}

export function getPartnerOpenid() {
  const user = getPartnerUser()
  return user?.openid || ''
}

export function hasPartnerOpenid() {
  return !!getPartnerOpenid()
}

export function isPartnerLoggedIn(store) {
  if (isPartnerWxWorkMode(store)) {
    return !!getPartnerCrmUserId(store)
  }
  const user = getPartnerUser()
  return !!(user && user.userId)
}

export function isPartnerRegistered() {
  if (isPartnerWxWorkMode()) {
    return true
  }
  const user = getPartnerUser()
  return !!(user && user.userId && user.registered !== false)
}

/** 与旧版业务员本地调试一致：非 production 或 ?lbbSource=test */
export function isPartnerDevMockEnabled() {
  return import.meta.env.MODE !== 'production' || getQueryString('lbbSource') === 'test'
}

/** 仅 ?partnerMock=1 时注入 mock 友商用户（避免覆盖 CRM 只读调试） */
export function shouldApplyPartnerDevMock() {
  return isPartnerDevMockEnabled() && getQueryString('partnerMock') === '1'
}

/** 本地调试注入 mock 友商用户，跳过 OAuth */
export function applyPartnerDevMock() {
  if (!shouldApplyPartnerDevMock()) return false
  if (getPartnerUser()?.userId) return true

  const userId = 3
  const registered = getQueryString('partnerRegistered') !== '0'
  setPartnerUser({
    userId,
    openid: 'oUpF8uMuAJO_M2pxb1Q9zNjWeS6o',
    unionid: 'oUpF8uMuAJO_M2pxb1Q9zNjWeS6o',
    registered,
    name: '本地测试友商',
  })
  console.log('[partner] 本地调试 mock 用户已注入', getPartnerUser())
  return true
}

export function getPartnerRouteByUser(user) {
  if (!user || (!user.userId && !user.openid)) {
    return 'partner'
  }
  // 仅有 openid、无 userId 视为未注册，需补充信息
  if (user.registered === false || !user.userId) {
    return 'partner-register'
  }
  return 'partner-customerList'
}

export function hasPartnerWxIdentity(user) {
  return !!(user && user.openid)
}

export function redirectPartnerByUser(vm, user) {
  const routeName = getPartnerRouteByUser(user)
  if (vm.$route.name !== routeName) {
    vm.$router.replace({ name: routeName })
  }
}

function safeReplace(vm, routeName) {
  if (vm.$route.name !== routeName) {
    vm.$router.replace({ name: routeName })
  }
}

function shouldRedirectToWapLogin() {
  if (getQueryString('forceWapLogin') === '1') return true
  if (isPartnerDevMockEnabled() && window.location.host.includes('localhost')) {
    return false
  }
  return true
}

function bootstrapPartnerCrmAccess(vm) {
  if (!isPartnerWxWorkMode(vm.$store)) return false
  if (vm.$route.name === 'partner' || vm.$route.name === 'partner-register') {
    safeReplace(vm, 'partner-customerList')
  }
  return true
}

/**
 * 进入友商业务页前的鉴权：
 * 1. 企微 CRM 只读（优先，避免 dev mock 覆盖）
 * 2. 本地 mock 友商（?partnerMock=1）
 * 3. wap OAuth 回跳 / session 友商
 * 4. 外部微信未登录 → wap OAuth
 */
export async function bootstrapPartnerAccess(vm) {
  if (getQueryString('from') === 'crm') {
    markPartnerCrmEntry()
  }

  if (bootstrapPartnerCrmAccess(vm)) {
    return true
  }

  if (applyPartnerDevMock()) {
    const user = getPartnerUser()
    if (user?.userId) {
      if (user.registered === false) {
        safeReplace(vm, 'partner-register')
        return false
      }
      if (vm.$route.name === 'partner') {
        redirectPartnerByUser(vm, user)
      }
      return true
    }
  }

  const urlUser = parsePartnerAuthFromUrl()
  if (urlUser?.openid) {
    setPartnerUser(urlUser)
    markPartnerOAuthBackLock()
    const user = getPartnerUser()
    const routeName = getPartnerRouteByUser(user)
    // 硬 replace 到无 query 的业务页：openid 回跳 URL 不留在地址栏/历史当前帧
    // 微信授权中间页仍在更早的跨域历史里，靠 installPartnerOAuthBackLock 吞返回
    if (replaceToPartnerPath(routeName)) {
      return false
    }
    installPartnerOAuthBackLock()
    if (!user?.userId || user?.registered === false) {
      return false
    }
    return true
  }

  const user = getPartnerUser()
  if (user && (user.userId || user.openid)) {
    installPartnerOAuthBackLock()
    if (!user.userId || user.registered === false) {
      safeReplace(vm, 'partner-register')
      return false
    }
    if (vm.$route.name === 'partner') {
      redirectPartnerByUser(vm, user)
    }
    return true
  }

  if (isWxWork()) {
    if (bootstrapPartnerCrmAccess(vm)) {
      return true
    }
    return false
  }

  // OAuth 失败回跳时留在登录页，由用户手动重试
  if (vm.$route.name === 'partner' && getQueryString('oauthFail') === '1') {
    return false
  }

  // /partner 入口页不自动跳 wap，由用户点击「微信登录」
  if (
    vm.$route.name !== 'partner' &&
    shouldRedirectToWapLogin() &&
    isWeiXin() &&
    !hasPartnerWxIdentity(getPartnerUser())
  ) {
    redirectToWapPartnerLogin(getPartnerOAuthBackUrl(vm))
    return false
  }

  if (vm.$route.name !== 'partner') {
    safeReplace(vm, 'partner')
  }
  return false
}

export function ensurePartnerAccess(vm) {
  return bootstrapPartnerAccess(vm)
}
